<template>
    <component
        :is="tooltipComponent"
        v-bind="tooltipProps"
    >
        <IconButton
            icon="tag"
            :class="$style.button_override"
            color="carbon-400"
            :aria-label="$tr('gameContainer.share')"
            interfaceTarget="copy-export-game-link"
            @click="copyToClipboard(exportUrl)"
        />

        <template #content>
            <TooltipContent
                :title="$tr('gameContainer.partner_popup_tooltip_title')"
                :description="$tr('gameContainer.partner_popup_tooltip_description')"
            >
                <template #image>
                    <div :class="$style.image_container">
                        <img
                            :src="props.gameIcon"
                            :class="$style.game_icon"
                            :alt="exportUrl"
                        />
                        <div :class="$style.circle_on_icon">
                            <Icon
                                name="tag"
                                :class="$style.icon_override"
                            />
                        </div>
                    </div>
                </template>
                <div
                    :class="$style.copy_section"
                    data-interface-target="copy-export-game-link-tooltip"
                    @click="copyToClipboard(exportUrl)"
                >
                    <Typography
                        size="s"
                        type="label"
                        :class="$style.link"
                        :responsive="false"
                    >
                        {{ copied ? $tr('gameContainer.share_popup_tooltip_copied') : displayedExportUrl }}
                    </Typography>
                    <IconButton
                        color="carbon-400"
                        size="s"
                        interfaceTarget="copy-export-game-link-tooltip"
                        :class="$style.button_round_override"
                        icon="copy"
                    />
                </div>
                <Button
                    color="purple-400"
                    size="m"
                    interfaceTarget="goto-start-monetizing-link-tooltip"
                    :class="$style.button_start_override"
                    :externalTo="partnerUrl"
                    target="_blank"
                    label="Start monetizing"
                />
            </TooltipContent>
        </template>
    </component>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core'
import { computed, toRef } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import IconButton from '@/components/Buttons/IconButton.vue'
import Icon from '@/components/Icon/Icon.vue'
import TooltipPopup from '@/components/Tooltip/TooltipPopup.vue'
import Typography from '@/components/Typography.vue'
import { useClipboard } from '@/composables/use-clipboard'

import { usePartnerUrls } from '../../../composables/use-partner-urls'
import TooltipContent from './TooltipContent.vue'

const props = defineProps<{
    gameIcon: string
    hru: string
    className: string
}>()

const isMounted = useMounted()

const { exportUrl, partnerUrl } = usePartnerUrls(toRef(() => props.hru))
const { copied, copyToClipboard } = useClipboard()

const tooltipComponent = computed(() => (isMounted.value ? TooltipPopup : 'div'))

const tooltipProps = computed(() =>
    isMounted.value
        ? {
              interfaceTarget: 'partner-button-tooltip',
              triggerClass: props.className,
          }
        : {
              class: props.className,
          },
)

const displayedExportUrl = computed(() => {
    try {
        const url = new URL(exportUrl.value || '')
        return url.href.replace(`${url.protocol}//`, '')
    } catch {
        return exportUrl.value || ''
    }
})
</script>

<style module>
.image_container {
    width: 112px;
    height: 112px;
    padding: 4px;
    position: relative;
}

.circle_on_icon {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--electric-lime);
    display: flex;
    justify-content: center;
    align-items: center;
}

.button_start_override {
    width: 100%;
}

.button_round_override {
    width: 40px;
    height: 40px;
}

.button_override {
    color: #fff;
    background-color: var(--carbon-400);
    border-radius: 20px;
    height: 40px;
    width: 40px;
    box-sizing: border-box;
}

.icon_override {
    color: #000000;
    width: 24px;
    height: 24px;
}

.game_icon {
    width: 112px;
    height: 112px;
    border-radius: 24px;
}

.copy_section {
    display: flex;
    gap: 16px;
    height: 48px;
    padding: 0 4px 0 12px;
    align-items: center;
    align-self: stretch;
    border-radius: 1000px;
    background: var(--carbon-200);
    cursor: pointer;
}

.link {
    flex: 1;
    color: #ffffff;
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
